import React, { useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import moment from 'moment';

import './DonationDetailsDrawer.custom.css';
import { Offcanvas, OffcanvasHeader, OffcanvasBody } from 'reactstrap';
import { Button, FormGroup, Label, Input, Badge } from 'reactstrap';
import { updateStatus } from '../actions/action';
import toast, { Toaster } from 'react-hot-toast';



	export const RemarksHistoryOffcanvasPortal = ({ isOpen, toggle, item, data }) => {
		return ReactDOM.createPortal(
			<>
				<div 
					className={`custom-history-drawer-backdrop ${isOpen ? 'show' : ''}`} 
					onClick={toggle}
				/>
				<div className={`custom-history-drawer ${isOpen ? 'show' : ''}`}>
					<div className="offcanvas-header">
						<h5 className="offcanvas-title">History</h5>
						<button type="button" className="btn-close" aria-label="Close" onClick={toggle}></button>
					</div>
					<div className="offcanvas-body">
						{item && (
							<div className="history-item-header">
								<div className="row">
                                    <div className="col-6 mb-2">
                                        <span className="history-item-label">Category</span>
                                        <div className="history-item-value">{item.item_category || '-'}</div>
                                    </div>
                                    <div className="col-6 mb-2">
                                        <span className="history-item-label">Type</span>
                                        <div className="history-item-value">{item.item_type || '-'}</div>
                                    </div>
                                </div>
							</div>
						)}
						{data && data.length > 0 ? (
							<div className="history-timeline">
                                {data.map((h, i) => (
                                    <div className="history-item" key={i}>
                                        <div className="history-item-date">{h.created_at ? moment(h.created_at).format('MMM D, YYYY h:mm A') : '-'}</div>
                                        <div className="history-item-content">
                                            <p>{h.item_remarks || 'No remarks recorded.'}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
						) : (
							<div className="empty-history">
                                <i className="mdi mdi-history"></i>
                                <p>No remarks history found for this item.</p>
                            </div>
						)}
					</div>
				</div>
			</>,
			document.body
		);
	};

const DonationDetailsDrawer = ({ open, onClose, donation, currentStatus, onShowHistory, onShowImage, refreshData }) => {


	// Track which row is updating (null if none)
	const [updatingIdx, setUpdatingIdx] = useState(null);

	// Columns for items table (matching API response keys)
	const itemColumns = [
		{ key: 'item_category', label: 'Category' },
		{ key: 'item_type', label: 'Type' },
		{ key: 'item_age', label: 'Age' },
		{ key: 'item_condition', label: 'Condition' },
		{ key: 'item_qty', label: 'Qty' },
		{ key: 'item_status', label: 'Status' },
		{ key: 'item_remarks', label: 'Remarks' },
	];

    const statusOptions = [ 
        { label: 'Pending', value: 'pending' }, 
        { label: 'Received', value: 'received' }, 
        { label: 'Pending for Refurbishment', value: 'pending for refurbishment' }, 
        { label: 'Refurbished', value: 'refurbished' }, 
        { label: 'Ready for Delivery', value: 'ready for delivery' }, 
        { label: 'Delivered', value: 'delivered' }
    ];
    
	const getInitialItems = () => {
		if (Array.isArray(donation?.data) && donation.data.length > 0) {
			return donation.data;
		} else if (Array.isArray(donation?.items) && donation.items.length > 0) {
			return donation.items;
		} else if (
			donation?.item_category ||
			donation?.item_type ||
			donation?.item_age ||
			donation?.item_condition ||
			donation?.item_qty ||
			donation?.item_image
		) {
			return [donation];
		}
		return [];
	};
	const [items, setItems] = useState(getInitialItems);
	// Update items when donation changes
	React.useEffect(() => {
		setItems(getInitialItems());
	}, [donation]);
	// Handler to update item status in the items array
	const handleItemStatusChange = (idx, newStatus) => {
		const updatedItems = items.map((item, i) =>
			i === idx ? { ...item, item_status: newStatus } : item
		);
		setItems(updatedItems);
	};

    const handleRemarksChange = (idx, newRemarks) => {
        const updatedItems = items.map((item, i) =>
			i === idx ? { ...item, item_remarks: newRemarks } : item
		);
        setItems(updatedItems);
    }

	const handleReceivedQtyChange = (idx, newQty) => {
		const updatedItems = items.map((item, i) =>
			i === idx ? { ...item, received_item_qty: newQty } : item
		);
		setItems(updatedItems);
	};
	if (!open || !donation) return null;

	// Save handler: push items (statuses) and remarks to API
	const handleUpdate = async (idx) => {
		setUpdatingIdx(idx);
		try {
			const item = items[idx];
			const payload = {
				donation_id: item.donation_id,
				item_id: item.item_id,
				item_status: item.item_status,
				item_remarks: item.item_remarks,
				received_item_qty: item.received_item_qty
			};

			const response = await updateStatus({
				params: payload
			});
			if (response?.code === 100) {
				toast.success(response?.message || 'Technician assigned successfully.');
				if (refreshData) {
					refreshData({ donation_id: item.donation_id });
				}
				// Update the items table with the latest data for this item and clear remarks
				setItems(prevItems =>
					prevItems.map((itm, i) =>
						i === idx
							? { ...itm, ...payload, item_remarks: '' }
							: itm
					)
				);
			} else {
				toast.error(response?.message);
			}
		} catch (err) {
			toast.error(err?.message || 'Failed to update donation item.');
			// Handle error (show notification, etc.)
		} finally {
			setUpdatingIdx(null);
		}
	};

	return (
		<div className="donation-list-drawer" >
			<div className="donation-list-drawer-header">
				<h5 className="donation-list-drawer-title">Donation Item Details</h5>
				<button className="donation-list-drawer-close" aria-label="Close" onClick={onClose}>&times;</button>
			</div>
			<div className="donation-list-drawer-body">
				{items.length > 0 && (
				<div className="donation-list-drawer-section">
					<div className="donation-list-drawer-section-title">
						<span className="mdi mdi-package-variant-closed donation-list-drawer-section-icon"></span> Item Information
					</div>
					<div style={{ overflowX: 'auto' }}>
						<table className="table table-bordered table-striped mb-4 donation-list-drawer-table" style={{ minWidth: 700 }}>
							<thead>
								<tr>
									{itemColumns.map(col => (
										<th key={col.key}>{col.label}</th>
									))}
									{/* Conditionally show Received Qty column if any item is 'received' */}
									{items.some(item => (item.item_status || '').toLowerCase() !== 'pending') && (
										<th key="recieved_item_qty">Received Qty</th>
									)}
                                    <th>Action</th>
								</tr>
							</thead>
							<tbody>
								{items.map((item, idx) => (
									<tr key={idx} className='align-middle'>
										{itemColumns.map(col => (
											<td key={col.key}>
												{col.key === 'item_category' ? (
													<>
														<div>{item[col.key] || '-'}</div>
														{item.item_image && (
															<div className="mt-1">
																<span 
																	className="btn btn-sm btn-outline-info waves-effect waves-light"
																	onClick={() => onShowImage(item.item_image)}
																	title="View Image"
																	style={{ padding: '2px 8px', fontSize: '11px' }}
																>
																	<i className="mdi mdi-eye-outline me-1"></i>View
																</span>
															</div>
														)}
													</>
												) : col.key === 'item_status' ? (
													<Input
														type="select"
														value={item[col.key] || ''}
														onChange={e => handleItemStatusChange(idx, e.target.value)}
														style={{ minWidth: 120, fontSize: 13 }}
														disabled={item.item_status === 'delivered'}
													>
														{statusOptions && statusOptions.map(opt => (
															<option key={opt.value || opt} value={opt.value || opt}>
																{opt.label || opt}
															</option>
														))}
													</Input>
												) : (col.key === 'item_remarks') ? (
													<Input
														type="textarea"
														value={item[col.key] || ''}
														onChange={e => handleRemarksChange(idx, e.target.value)}
														style={{ minWidth: 120, fontSize: 13, height: '32px', }}
														disabled={item.item_status === 'delivered'}
													></Input>
												) : (
													item[col.key] || '-'
												)}
											</td>
										))}
										{/* Show Received Qty input only if status is 'received' */}
										{item.item_status.toLowerCase() !== 'pending' && (
											<td key="received_item_qty" >
												<Input
													type="number"
													min={0}
													value={item.received_item_qty || ''}
													onChange={e => handleReceivedQtyChange(idx, e.target.value)}
													style={{ minWidth: 80, fontSize: 13, lineHeight: 1 }}
													disabled={item.item_status.toLowerCase() === 'pending for refurbishment' || item.item_status.toLowerCase() === 'refurbished' || item.item_status.toLowerCase() === 'ready for delivery' || item.item_status.toLowerCase() === 'delivered'}
												/>
											</td>
										)}
										<td className='text-center'>
											{item.item_status.toLowerCase() !== 'delivered' && (
												<Button key={idx} color="primary" size="sm" onClick={() => handleUpdate(idx)} disabled={updatingIdx === idx}>{updatingIdx === idx ? 'Updating...' : 'Update'}</Button>
											)}
											<Button key={idx} color="secondary" size="sm" className="ms-2" onClick={() => onShowHistory(item)}>History</Button>
										</td>

									</tr>
								))}
							</tbody>
						</table>
					</div>
				</div>
				)}
				<div className="donation-list-drawer-actions">
					<Button color="secondary" outline onClick={onClose}>Close</Button>
				</div>
			</div>
			<Toaster position="top-right" reverseOrder={false} />

			{/* Remarks History Offcanvas is now exported as a portal. Render it at the root of your app or page, not here. */}
		</div>
	);
};

export default DonationDetailsDrawer;
